/**
 * Domain Entity: Document
 * Represents a document with business logic
 */
export class Document {
  private constructor(
    private readonly _id: string,
    private readonly _userId: string,
    private readonly _name: string,
    private readonly _type: string,
    private readonly _size: number,
    private readonly _url: string,
    private readonly _createdAt: Date = new Date(),
    private readonly _updatedAt: Date = new Date(),
    private readonly _isPublic: boolean = false,
    private readonly _tags: string[] = []
  ) {}

  static create(
    id: string,
    userId: string,
    name: string,
    type: string,
    size: number,
    url: string,
    isPublic: boolean = false,
    tags: string[] = []
  ): Document {
    if (!id || !userId || !name || !type || !url) {
      throw new Error('Document ID, User ID, name, type, and URL are required');
    }

    if (size < 0) {
      throw new Error('Document size cannot be negative');
    }

    if (size > 100 * 1024 * 1024) { // 100MB limit
      throw new Error('Document size cannot exceed 100MB');
    }

    return new Document(id, userId, name, type, size, url, new Date(), new Date(), isPublic, tags);
  }

  static fromData(data: {
    id: string;
    user_id: string;
    name: string;
    type: string;
    size: number;
    url: string;
    created_at?: string;
    updated_at?: string;
    is_public?: boolean;
    tags?: string[];
  }): Document {
    return new Document(
      data.id,
      data.user_id,
      data.name,
      data.type,
      data.size,
      data.url,
      data.created_at ? new Date(data.created_at) : new Date(),
      data.updated_at ? new Date(data.updated_at) : new Date(),
      data.is_public || false,
      data.tags || []
    );
  }

  get id(): string {
    return this._id;
  }

  get userId(): string {
    return this._userId;
  }

  get name(): string {
    return this._name;
  }

  get type(): string {
    return this._type;
  }

  get size(): number {
    return this._size;
  }

  get url(): string {
    return this._url;
  }

  get createdAt(): Date {
    return this._createdAt;
  }

  get updatedAt(): Date {
    return this._updatedAt;
  }

  get isPublic(): boolean {
    return this._isPublic;
  }

  get tags(): string[] {
    return [...this._tags];
  }

  get sizeInMB(): number {
    return this._size / (1024 * 1024);
  }

  get sizeInKB(): number {
    return this._size / 1024;
  }

  get extension(): string {
    return this._name.split('.').pop() || '';
  }

  isOwner(userId: string): boolean {
    return this._userId === userId;
  }

  canBeAccessedBy(userId: string): boolean {
    return this.isOwner(userId) || this._isPublic;
  }

  isImage(): boolean {
    const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
    return imageTypes.includes(this.extension.toLowerCase());
  }

  isVideo(): boolean {
    const videoTypes = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'];
    return videoTypes.includes(this.extension.toLowerCase());
  }

  isPDF(): boolean {
    return this.extension.toLowerCase() === 'pdf';
  }

  addTag(tag: string): Document {
    if (!this._tags.includes(tag)) {
      return new Document(
        this._id,
        this._userId,
        this._name,
        this._type,
        this._size,
        this._url,
        this._createdAt,
        new Date(),
        this._isPublic,
        [...this._tags, tag]
      );
    }
    return this;
  }

  removeTag(tag: string): Document {
    return new Document(
      this._id,
      this._userId,
      this._name,
      this._type,
      this._size,
      this._url,
      this._createdAt,
      new Date(),
      this._isPublic,
      this._tags.filter(t => t !== tag)
    );
  }

  toJSON() {
    return {
      id: this._id,
      user_id: this._userId,
      name: this._name,
      type: this._type,
      size: this._size,
      url: this._url,
      created_at: this._createdAt.toISOString(),
      updated_at: this._updatedAt.toISOString(),
      is_public: this._isPublic,
      tags: this._tags
    };
  }
}
